import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { withApplicationInsights } from "../../../components/telemetry";
import { Coachee } from "../../../models";
import { CoacheeService } from "../../../services/coachingNavigator/coacheeService";
import config from "../../../config";
import CreateClientPanel from "../../../components/coachingNavigator/createClientPanel";
import { formatHoursDisplay } from "../../../util/formatHours";
import { Context } from "../../../components/context";
import { CoachingPageHeader } from "../../../components/coachingNavigator/coachingPageHeader";
import ConfirmationPanel from "../../../components/confirmationPanel";

const ClientPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const appContext = React.useContext(Context);
  const [clients, setClients] = useState<Coachee[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Coachee | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Coachee | null>(null);
  const [purgeTarget, setPurgeTarget] = useState<Coachee | null>(null);
  const [purgeConfirmName, setPurgeConfirmName] = useState("");

  const coacheeService = useMemo(
    () => new CoacheeService(config.api.baseUrl, "/coachee"),
    [],
  );

  useEffect(() => {
    let isActive = true;
    setLoading(true);

    const creatorEmail = (appContext.state.invoker?.email || "").trim();
    if (!creatorEmail) {
      setClients([]);
      setLoading(false);
      return () => {
        isActive = false;
      };
    }

    coacheeService
      .getByCreator(creatorEmail)
      .then((data) => {
        if (!isActive) return;
        setClients(
          (data || []).sort((a, b) =>
            (a.name || "").localeCompare(b.name || ""),
          ),
        );
      })
      .catch((error) => {
        console.error("Failed to load clients", error);
      })
      .finally(() => {
        if (isActive) setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [coacheeService, appContext.state.invoker]);

  const handleCreated = useCallback((coachee: Coachee) => {
    setClients((prev) =>
      [...prev, coachee].sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
    );
    setCreateOpen(false);
  }, []);

  const handleSaved = useCallback((coachee: Coachee) => {
    setClients((prev) =>
      prev
        .map((c) => (c.id === coachee.id ? coachee : c))
        .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    );
    setCreateOpen(false);
    setEditing(null);
  }, []);

  const handleCloseCreate = useCallback(() => {
    setCreateOpen(false);
    setEditing(null);
  }, []);

  const handleDelete = useCallback(async () => {
    if (!deleteTarget?.id) return;
    try {
      await coacheeService.delete(deleteTarget.id);
      setClients((prev) => prev.filter((c) => c.id !== deleteTarget.id));
    } catch (error) {
      console.error("Failed to delete client", error);
    }
    setDeleteTarget(null);
  }, [deleteTarget, coacheeService]);

  const handlePurge = useCallback(() => {
    if (!deleteTarget) return;
    setPurgeTarget(deleteTarget);
    setPurgeConfirmName("");
    setDeleteTarget(null);
  }, [deleteTarget]);

  const handlePurgeConfirm = useCallback(async () => {
    if (!purgeTarget?.id) return;
    try {
      await coacheeService.purge(purgeTarget.id);
      await coacheeService.save({
        id: purgeTarget.id,
        name: purgeTarget.name,
        email: "",
        bookedHours: 0,
        completedHours: 0,
      } as Coachee);
      setClients((prev) =>
        prev.map((c) =>
          c.id === purgeTarget.id
            ? {
                ...c,
                email: "",
                bookedHours: 0,
                completedHours: 0,
                bookedSessions: 0,
                completedSessions: 0,
              }
            : c,
        ),
      );
    } catch (error) {
      console.error("Failed to purge client", error);
    }
    setPurgeTarget(null);
    setPurgeConfirmName("");
  }, [purgeTarget, coacheeService]);

  const openCreate = useCallback(() => {
    setEditing(null);
    setCreateOpen(true);
  }, []);

  const openEdit = useCallback((client: Coachee) => {
    setEditing(client);
    setCreateOpen(true);
  }, []);

  const clientsEmpty = !loading && clients.length === 0;

  return (
    <Box sx={{ pt: 2 }}>
      <CoachingPageHeader
        onBack={() => navigate("/management")}
        title={t("coachingNavigator.clients.title")}
        subtitle={t("coachingNavigator.clients.subtitle")}
        rightContent={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: "none" }}
          >
            {t("coachingNavigator.clients.add")}
          </Button>
        }
        sx={{ mb: 1 }}
      />
      <Box sx={{ mt: 1 }}>
        {loading ? (
          <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
            <Typography variant="body2">
              {t("coachingNavigator.clients.loading")}
            </Typography>
          </Box>
        ) : clientsEmpty ? (
          <Box sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
            <Typography variant="body1">
              {t("coachingNavigator.clients.emptyTitle")}
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {t("coachingNavigator.clients.emptySubtitle")}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {clients.map((client) => (
              <Card
                key={client.id}
                variant="outlined"
                sx={{
                  borderColor: "divider",
                  backgroundColor: "white",
                  borderRadius: 3,
                  boxShadow: 0,
                  transition: "all 0.25s ease-out",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: 4,
                    borderColor: alpha(theme.palette.primary.main, 0.4),
                    ".client-actions": { opacity: 1 },
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 2,
                    "&:last-child": { pb: 2 },
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 2,
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="subtitle1"
                        color="primary"
                        onClick={() => navigate(`/coaching-hub/${client.id}`)}
                        sx={{
                          fontWeight: 600,
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" }
                        }}>
                        {client.name}
                      </Typography>
                      <Chip
                        size="small"
                        color={client.active ? "success" : "warning"}
                        label={
                          client.active
                            ? t("coachingNavigator.active")
                            : t("coachingNavigator.inactive")
                        }
                      />
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <EmailOutlinedIcon fontSize="small" />
                      <Typography variant="body2">{client.email}</Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        color: "text.secondary",
                      }}
                    >
                      <ScheduleOutlinedIcon fontSize="small" />
                      <Typography variant="body2">
                        {t("coachingNavigator.bookedHours")}:{" "}
                        {formatHoursDisplay(client.bookedHours)} |{" "}
                        {t("coachingNavigator.completedHours")}:{" "}
                        {formatHoursDisplay(client.completedHours)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{
                      color: "text.secondary"
                    }}>
                      {t("coachingNavigator.bookedSessions")}:{" "}
                      {client.bookedSessions ?? 0} |{" "}
                      {t("coachingNavigator.completedSessions")}:{" "}
                      {client.completedSessions ?? 0}
                    </Typography>
                  </Box>

                  <Box
                    className="client-actions"
                    sx={{
                      display: "flex",
                      gap: 0.5,
                      opacity: 0,
                      transition: "opacity 0.25s ease-out",
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/coaching-hub/${client.id}`)}
                      title={t("coachingNavigator.clients.viewDetails")}
                    >
                      <VisibilityOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => navigate(`/sessionOverview/${client.id}`)}
                      title={t("coachingNavigator.sessionOverview")}
                    >
                      <EventNoteOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => openEdit(client)}>
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => setDeleteTarget(client)}
                    >
                      <DeleteOutlineOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </Box>
      <CreateClientPanel
        open={createOpen}
        onClose={handleCloseCreate}
        onCreated={handleCreated}
        initialCoachee={editing}
        onSaved={handleSaved}
      />
      <ConfirmationPanel
        showModal={!!deleteTarget}
        hideModal={() => setDeleteTarget(null)}
        confirmModal={handleDelete}
        entry={{ email: deleteTarget?.email, name: deleteTarget?.name }}
        type="user"
        action="delete"
        title={t("coachingNavigator.clientOverview.deleteConfirmTitle")}
        content={t("coachingNavigator.clientOverview.deleteConfirmContent")}
        customActions={
          <>
            <Button
              variant="outlined"
              onClick={() => setDeleteTarget(null)}
              sx={{ minHeight: 12, whiteSpace: "nowrap" }}
            >
              {t("coachingNavigator.clientOverview.cancel")}
            </Button>
            <Button
              variant="outlined"
              color="warning"
              onClick={handlePurge}
              sx={{ minHeight: 12, whiteSpace: "nowrap" }}
            >
              {t("coachingNavigator.clientOverview.purgeData")}
            </Button>
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              sx={{ minHeight: 12, whiteSpace: "nowrap" }}
            >
              {t("coachingNavigator.clientOverview.deleteClient")}
            </Button>
          </>
        }
      />
      {/* Custom Purge Confirmation Dialog */}
      <Dialog
        open={!!purgeTarget}
        onClose={() => {
          setPurgeTarget(null);
          setPurgeConfirmName("");
        }}
        fullWidth
        maxWidth="xs" slotProps={{ paper: { sx: { backgroundColor: "white", borderRadius: 2 } } }}
      >
        <DialogTitle>
          {t("coachingNavigator.clientOverview.purgeData")}
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              mb: 2
            }}>
            {t("coachingNavigator.clientOverview.purgeConfirmBody_before")}
            <span style={{ fontWeight: 600 }}>{purgeTarget?.name}</span>
            {t("coachingNavigator.clientOverview.purgeConfirmBody_after")}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={purgeTarget?.name || ""}
            value={purgeConfirmName}
            onChange={(e) => setPurgeConfirmName(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setPurgeTarget(null);
              setPurgeConfirmName("");
            }}
          >
            {t("button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={purgeConfirmName !== purgeTarget?.name}
            onClick={handlePurgeConfirm}
          >
            {t("button.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default withApplicationInsights(ClientPage, "ClientPage");
